import type { Agent, Persisted } from '@tunasong/schemas'

/**
 * Returns the later of two optional ISO-8601 strings; undefined if both
 * are undefined. ISO-8601 lexicographic comparison matches chronological
 * order so we don't need to round-trip through `new Date()`.
 */
export const maxIso = (a?: string, b?: string): string | undefined => {
  if (!a) {return b}
  if (!b) {return a}
  return a > b ? a : b
}

/**
 * Derive whether the Agent Rail should render an "unread" dot for `agent`,
 * given the current user's per-agent read cursors.
 *
 * Pure function — no React. Joni's rail card reads this via a thin React
 * wrapper that pulls cursors from `useUserConfig('agentReadCursors')`.
 * Splitting the derivation lets us test it without happy-dom and reuse
 * it from non-React contexts (e.g. the bell consolidation work).
 *
 * Rule:
 *  - Latest activity = max(lastRunAt, lastNudgeAt). `runtimeStatus`-driven
 *    "error" does NOT contribute — the rail surfaces errors separately
 *    (per Joni's spec: red dot vs unread dot).
 *  - No activity at all → not unread.
 *  - No cursor stored → not unread (first-ever-visit suppression; the
 *    caller's React wrapper initializes the cursor lazily on first
 *    render so the dot doesn't flash for already-old activity).
 *  - Otherwise: unread = latestActivity > cursor.
 */
export const deriveAgentUnread = (
  agent: Pick<Persisted<Agent>, 'id' | 'lastRunAt' | 'lastNudgeAt'>,
  cursor: string | undefined
): boolean => {
  const latestActivity = maxIso(agent.lastRunAt, agent.lastNudgeAt)
  if (!latestActivity) {return false}
  if (!cursor) {return false}
  return latestActivity > cursor
}
